import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { GraduationCap, Trophy, Target, Star, Clock, BookOpen, Users, ChevronRight, AlertCircle, Sparkles, CreditCard, AlertTriangle, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ParentLayout } from "@/components/parent/ParentLayout";
import { GetTheAppCard } from "@/components/AppStoreBadges";

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  grade: number;
  school: string | null;
  profilePhotoUrl: string | null;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  subscriptionEndDate: string | null;
  lastActiveAt: string | null;
  totalPoints: number;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
  currentLevel: number;
  totalXP: number;
  nextLevelXP: number;
  testsCompleted: number;
}

function subBadgeVariant(s: string): "default" | "secondary" | "destructive" | "outline" {
  if (s === "active" || s === "trial" || s === "trialing") return "default";
  if (s === "past_due") return "destructive";
  return "outline";
}

function subLabel(s: string): string {
  switch (s) {
    case "active": return "Active";
    case "trial":
    case "trialing": return "Trial";
    case "past_due": return "Payment failed";
    case "cancelled": return "Cancelled";
    case "expired": return "Expired";
    case "inactive": return "Inactive";
    case "paused": return "Paused";
    default: return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

interface Insight {
  priority: number;
  type: string;
  childId: string;
  childName: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
}

const INSIGHT_ICON: Record<string, any> = {
  payment_failed: AlertCircle,
  sub_inactive: CreditCard,
  cancel_pending: AlertTriangle,
  inactive: Clock,
  never_active: Sparkles,
  weak_subject: BookOpen,
};

export default function ParentDashboardPage() {
  const { data, isLoading } = useQuery<{ children: Child[] }>({
    queryKey: ["/api/parent/children"],
  });
  const { data: insightsData } = useQuery<{ insights: Insight[] }>({
    queryKey: ["/api/parent/insights"],
  });

  const children = data?.children ?? [];
  const insights = insightsData?.insights ?? [];
  const activeCount = children.filter((c) => c.subscriptionStatus === "active" || c.subscriptionStatus === "trial" || c.subscriptionStatus === "trialing").length;
  const alertCount = children.filter((c) => c.subscriptionStatus === "past_due" || c.subscriptionStatus === "expired").length;
  const inactiveDays = children.filter((c) => {
    if (!c.lastActiveAt) return true;
    const days = (Date.now() - new Date(c.lastActiveAt).getTime()) / (1000 * 60 * 60 * 24);
    return days > 7;
  }).length;

  return (
    <ParentLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Overview</h1>
          <p className="text-sm text-muted-foreground">Everything happening across your kids' accounts.</p>
        </div>

        <GetTheAppCard audience="parent" />

        {insights.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-muted-foreground">Do this next</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {insights.map((insight, idx) => {
                const Icon = INSIGHT_ICON[insight.type] ?? Sparkles;
                const isUrgent = insight.priority <= 1;
                return (
                  <Link key={`${insight.type}-${insight.childId}-${idx}`} href={insight.ctaUrl}>
                    <Card
                      className={`hover:shadow-sm transition-shadow cursor-pointer ${isUrgent ? "border-destructive/40 bg-destructive/5" : ""}`}
                      data-testid={`insight-${insight.type}-${insight.childId}`}
                    >
                      <CardContent className="p-3 flex items-start gap-3">
                        <div className={`shrink-0 rounded-md p-1.5 ${isUrgent ? "bg-destructive/10 text-destructive" : "bg-muted text-foreground"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{insight.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{insight.body}</p>
                          <p className="text-xs text-primary mt-1.5 inline-flex items-center gap-1">
                            {insight.ctaLabel}
                            <ChevronRight className="w-3 h-3" />
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Children</p>
              <p className="text-2xl font-bold mt-1" data-testid="stat-children">{isLoading ? "–" : children.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Active subs</p>
              <p className="text-2xl font-bold mt-1" data-testid="stat-active">{isLoading ? "–" : activeCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Inactive 7d+</p>
              <p className="text-2xl font-bold mt-1" data-testid="stat-inactive">{isLoading ? "–" : inactiveDays}</p>
            </CardContent>
          </Card>
          <Card className={alertCount > 0 ? "border-destructive" : ""}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Alerts</p>
              <p className="text-2xl font-bold mt-1 flex items-center gap-1.5" data-testid="stat-alerts">
                {isLoading ? "–" : alertCount}
                {alertCount > 0 && <AlertCircle className="h-4 w-4 text-destructive" />}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your children</h2>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i}>
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-9 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : children.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-3">
              <Users className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="font-semibold">No children linked yet</p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Once your child's account lists you as parent or guardian, they will show up here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {children.map((c) => {
              const initials = `${c.firstName.charAt(0)}${c.lastName.charAt(0)}`.toUpperCase();
              const xpPct = c.nextLevelXP > 0 ? Math.min(Math.round((c.totalXP / c.nextLevelXP) * 100), 100) : 0;
              return (
                <Card key={c.id} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-12 w-12">
                        {c.profilePhotoUrl && <AvatarImage src={c.profilePhotoUrl} alt={`${c.firstName} ${c.lastName}`} />}
                        <AvatarFallback>{initials}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">{c.firstName} {c.lastName}</CardTitle>
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <GraduationCap className="w-3.5 h-3.5" />
                          Grade {c.grade}{c.school ? ` · ${c.school}` : ""}
                        </p>
                      </div>
                      <Badge variant={subBadgeVariant(c.subscriptionStatus)}>{subLabel(c.subscriptionStatus)}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col space-y-4">
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-muted-foreground" /> Level {c.currentLevel}</span>
                        <span className="text-muted-foreground">{c.totalXP.toLocaleString()} / {c.nextLevelXP.toLocaleString()} XP</span>
                      </div>
                      <Progress value={xpPct} className="h-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                      <div className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-muted-foreground" /> {c.accuracy}% accuracy</div>
                      <div className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-muted-foreground" /> {c.totalQuestions} questions</div>
                      <div className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-muted-foreground" /> {c.totalPoints.toLocaleString()} pts</div>
                      <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-muted-foreground" /> {c.lastActiveAt ? formatDistanceToNow(new Date(c.lastActiveAt), { addSuffix: true }) : "Never"}</div>
                    </div>
                    <Button asChild variant="outline" className="w-full mt-auto">
                      <Link href={`/parent/children/${c.id}`} data-testid={`child-detail-link-${c.id}`}>
                        View details
                        <ChevronRight className="ml-1.5 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </ParentLayout>
  );
}
